interface ExportData {
  headers: string[];
  data: any[];
}

// CSV Export Utility
export function exportToCSV(data: ExportData, filename: string) {
  const csvContent = [
    data.headers.join(','),
    ...data.data.map(row => 
      data.headers.map(header => {
        const value = row[header];
        // Handle values that might contain commas
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// JSON Export Utility
export function exportToJSON(data: any, filename: string) {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.json`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Excel Export Utility (basic implementation)
export function exportToExcel(sheets: Array<{ name: string; data: ExportData }>, filename: string) {
  // For a full Excel implementation, you'd use a library like SheetJS
  // This is a simplified version that creates a CSV-like format
  const content = sheets.map(sheet => {
    const sheetContent = [
      `=== ${sheet.name.toUpperCase()} ===`,
      sheet.data.headers.join('\t'),
      ...sheet.data.data.map(row => 
        sheet.data.headers.map(header => row[header]).join('\t')
      ),
      ''
    ].join('\n');
    return sheetContent;
  }).join('\n');

  const blob = new Blob([content], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.xls`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// PDF Export Utility (basic implementation)
export function exportToPDF(data: ExportData, filename: string, title: string) {
  // For a full PDF implementation, you'd use a library like jsPDF
  // This is a simplified version that creates an HTML print layout
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        h1 { color: #333; border-bottom: 2px solid #ddd; padding-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f5f5f5; font-weight: bold; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        .timestamp { color: #666; font-size: 12px; margin-top: 20px; }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <table>
        <thead>
          <tr>
            ${data.headers.map(header => `<th>${header}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${data.data.map(row => `
            <tr>
              ${data.headers.map(header => `<td>${row[header] || ''}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
      <div class="timestamp">
        Generated on ${new Date().toLocaleString()}
      </div>
    </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
        URL.revokeObjectURL(url);
      }, 100);
    };
  }
}

// Analytics Export Functions
export function exportAnalyticsData(analytics: any, type: 'csv' | 'json' | 'excel' | 'pdf') {
  const timestamp = new Date().toISOString().split('T')[0];
  
  switch (type) {
    case 'csv':
      exportToCSV({
        headers: ['Metric', 'Value', 'Growth', 'Period'],
        data: [
          { 'Metric': 'Total Users', 'Value': analytics.totalUsers, 'Growth': `${analytics.userGrowthPercent}%`, 'Period': 'Monthly' },
          { 'Metric': 'Monthly Revenue', 'Value': `₹${analytics.monthlyRevenue}`, 'Growth': `${analytics.revenueGrowthPercent}%`, 'Period': 'Monthly' },
          { 'Metric': 'Active Models', 'Value': analytics.activeModels, 'Growth': 'N/A', 'Period': 'Current' },
          { 'Metric': 'Credits Used', 'Value': `${analytics.totalCreditsUsed}`, 'Growth': `${analytics.creditsGrowthPercent}%`, 'Period': 'Monthly' },
        ]
      }, `admin-analytics-${timestamp}`);
      break;
      
    case 'json':
      exportToJSON({
        exportDate: new Date().toISOString(),
        analytics: analytics,
        metadata: {
          source: 'StoryLikho Admin Console',
          version: '1.0',
          type: 'analytics'
        }
      }, `admin-analytics-${timestamp}`);
      break;
      
    case 'excel':
      exportToExcel([
        {
          name: 'Overview',
          data: {
            headers: ['Metric', 'Value', 'Growth', 'Period'],
            data: [
              { 'Metric': 'Total Users', 'Value': analytics.totalUsers, 'Growth': `${analytics.userGrowthPercent}%`, 'Period': 'Monthly' },
              { 'Metric': 'Monthly Revenue', 'Value': `₹${analytics.monthlyRevenue}`, 'Growth': `${analytics.revenueGrowthPercent}%`, 'Period': 'Monthly' },
            ]
          }
        }
      ], `admin-analytics-${timestamp}`);
      break;
      
    case 'pdf':
      exportToPDF({
        headers: ['Metric', 'Value', 'Growth', 'Period'],
        data: [
          { 'Metric': 'Total Users', 'Value': analytics.totalUsers, 'Growth': `${analytics.userGrowthPercent}%`, 'Period': 'Monthly' },
          { 'Metric': 'Monthly Revenue', 'Value': `₹${analytics.monthlyRevenue}`, 'Growth': `${analytics.revenueGrowthPercent}%`, 'Period': 'Monthly' },
        ]
      }, `admin-analytics-${timestamp}`, 'StoryLikho Admin Analytics Report');
      break;
  }
}

// User Data Export
export function exportUserData(users: any[], type: 'csv' | 'json' | 'excel') {
  const timestamp = new Date().toISOString().split('T')[0];
  const userData = {
    headers: ['ID', 'Name', 'Email', 'Plan', 'Credits', 'Status', 'Joined'],
    data: users.map(user => ({
      'ID': user.id,
      'Name': `${user.firstName} ${user.lastName}`,
      'Email': user.email,
      'Plan': user.planCode || 'FREE',
      'Credits': user.credits || 0,
      'Status': user.emailVerified ? 'Verified' : 'Unverified',
      'Joined': new Date(user.createdAt).toLocaleDateString()
    }))
  };

  switch (type) {
    case 'csv':
      exportToCSV(userData, `users-export-${timestamp}`);
      break;
    case 'json':
      exportToJSON({
        exportDate: new Date().toISOString(),
        users: userData.data,
        metadata: {
          source: 'StoryLikho Admin Console',
          totalUsers: users.length,
          type: 'user-export'
        }
      }, `users-export-${timestamp}`);
      break;
    case 'excel':
      exportToExcel([{
        name: 'Users',
        data: userData
      }], `users-export-${timestamp}`);
      break;
  }
}

// Coupon Data Export
export function exportCouponData(coupons: any[], type: 'csv' | 'json' | 'excel') {
  const timestamp = new Date().toISOString().split('T')[0];
  const couponData = {
    headers: ['Code', 'Name', 'Type', 'Value', 'Usage', 'Max Uses', 'Valid Until', 'Status'],
    data: coupons.map(coupon => ({
      'Code': coupon.code,
      'Name': coupon.name,
      'Type': coupon.discountType,
      'Value': coupon.discountValue,
      'Usage': coupon.currentUses || 0,
      'Max Uses': coupon.maxUses || 'Unlimited',
      'Valid Until': coupon.validUntil ? new Date(coupon.validUntil).toLocaleDateString() : 'No Expiry',
      'Status': coupon.isActive ? 'Active' : 'Inactive'
    }))
  };

  switch (type) {
    case 'csv':
      exportToCSV(couponData, `coupons-export-${timestamp}`);
      break;
    case 'json':
      exportToJSON({
        exportDate: new Date().toISOString(),
        coupons: couponData.data,
        metadata: {
          source: 'StoryLikho Admin Console',
          totalCoupons: coupons.length,
          type: 'coupon-export'
        }
      }, `coupons-export-${timestamp}`);
      break;
    case 'excel':
      exportToExcel([{
        name: 'Coupons',
        data: couponData
      }], `coupons-export-${timestamp}`);
      break;
  }
}